import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useDrop } from 'react-dnd';

import { nanoid } from 'nanoid';
import pullAt from 'lodash-es/pullAt';

import { getBase64ImageInfo } from '@ecp/common/src/utils/fileUtil';
import { DND_IMAGE_ICON, FILE_VALIDATION, IMAGE_STATUS } from '@ecp/common/src/const/imageFile';

const useImageFileUploader = (
  ref,
  imageFileList,
  setImageFileList,
  multiUploadMaxCount,
  maxSize,
  showAlertDialog,
  readOnly,
  multiUpload
) => {
  const fileUploaderRef = useRef(null);
  useImperativeHandle(ref, () => fileUploaderRef.current);
  const [, drop] = useDrop(() => ({ accept: DND_IMAGE_ICON }));
  const filteredImageFileList = useMemo(
    () =>
      imageFileList.filter(
        ({ status }) =>
          status === IMAGE_STATUS.REGISTERED || status === IMAGE_STATUS.NEW || status === IMAGE_STATUS.COPY
      ),
    [imageFileList]
  );

  const handleDelete = useCallback(
    (keyValue) => {
      const deleteImage = imageFileList.find(({ type }) => type === keyValue);
      if (!deleteImage) return;
      if (deleteImage.status === IMAGE_STATUS.REGISTERED) {
        setImageFileList(
          imageFileList.map((image) => ({
            ...image,
            status: image.type === deleteImage.type ? IMAGE_STATUS.NEED_TO_DEL : image.status,
          }))
        );
      } else {
        setImageFileList(imageFileList.filter((image) => image.type !== deleteImage.type));
      }
    },
    [imageFileList, setImageFileList]
  );

  const handleMove = (dragImageType, overImageType) => {
    const newImageFileList = [...imageFileList];
    const dragStartIndex = imageFileList.findIndex(({ type }) => type === dragImageType);
    const dragEndIndex = imageFileList.findIndex(({ type }) => type === overImageType);

    const [pulled] = pullAt(newImageFileList, dragStartIndex);
    newImageFileList.splice(dragStartIndex < dragEndIndex ? dragEndIndex + 1 : dragEndIndex, 0, pulled);

    setImageFileList(newImageFileList);
  };

  const handleMultiUpload = useCallback(
    async (e, fileList, result) => {
      if (result.errors.length > 0) {
        showAlertDialog?.(result.errors[0]);
      } else if (filteredImageFileList.length + fileList.length > multiUploadMaxCount) {
        showAlertDialog?.(FILE_VALIDATION.EXCEED_MAXIMUM_FILE_COUNT);
      } else if (
        filteredImageFileList.reduce((total, { size }) => total + size, 0) +
          fileList.reduce((total, { size }) => total + size, 0) >
        maxSize * (multiUploadMaxCount || 1)
      ) {
        showAlertDialog?.(FILE_VALIDATION.EXCEED_MAXIMUM_TOTAL_SIZE);
      } else {
        const newImageFileList = [
          ...(imageFileList || []),
          ...(await Promise.all(
            fileList.map(async (file) => {
              const imgInfo = await getBase64ImageInfo(file);
              return { ...imgInfo, file, type: nanoid(), status: IMAGE_STATUS.NEW };
            })
          )),
        ];
        setImageFileList(newImageFileList);
      }
      e.target.value = null;
    },
    [imageFileList, filteredImageFileList, multiUploadMaxCount, maxSize, setImageFileList, showAlertDialog]
  );

  const showDropzone = useMemo(
    () => !readOnly && multiUpload && filteredImageFileList.length < multiUploadMaxCount,
    [readOnly, multiUpload, filteredImageFileList.length, multiUploadMaxCount]
  );
  return { drop, filteredImageFileList, handleDelete, handleMove, handleMultiUpload, showDropzone };
};

export default useImageFileUploader;
